import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { SearchResult as SearchResultType } from "../types"
import { getTopEFPower } from "../utils/dataProcessor"
import { getDefaultAvatarPath } from "../utils/api"
import ShareModal from "./ShareModal"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import RankCard3D from "./RankCard3D"

interface SearchResultProps {
  result: SearchResultType
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
})

const SearchResult: React.FC<SearchResultProps> = ({ result }) => {
  const { t } = useTranslation()
  const [avatar, setAvatar] = useState<string | null>(null)
  const [topEFPower, setTopEFPower] = useState<number>(0)
  const defaultAvatar = getDefaultAvatarPath()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  useEffect(() => {
    getTopEFPower().then((score) => setTopEFPower(score))
  }, [])

  useEffect(() => {
    if (!result.found) setAvatar(defaultAvatar)

    if (result.found && result.data) {
      if (
        result.data.ensName.startsWith("0x") &&
        result.data.ensName.length > 16
      ) {
        setAvatar(defaultAvatar)
      } else {
        setAvatar(
          `https://metadata.ens.domains/mainnet/avatar/${result.data.ensName}`
        )
      }
    }
  }, [result, defaultAvatar])

  useEffect(() => {
    if (isShareModalOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isShareModalOpen])

  if (!result.found) {
    return (
      <div className="w-full max-w-xl mx-auto mt-6 overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="p-6 bg-gradient-to-br from-red-50 to-white">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              {t("search.notFound")}
            </h3>
            <p className="text-gray-600 max-w-sm">{t("search.tryAgain")}</p>
          </div>
        </div>
      </div>
    )
  }

  const { data } = result
  if (!data) return null

  return (
    <div data-search-result="true">
      <QueryClientProvider client={queryClient}>
        <RankCard3D data={data} />

        <div className="flex justify-center gap-4 mt-4 mb-16">
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="py-2 px-6 text-white rounded-lg bg-gradient-to-r from-emerald-600 to-green-500 hover:scale-105 transition-all"
          >
            {t("shareCard.share")}
          </button>
        </div>

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          data={data}
          topEFPower={topEFPower}
          avatar={avatar || defaultAvatar}
          defaultAvatar={defaultAvatar}
        />
      </QueryClientProvider>
    </div>
  )
}

export default SearchResult
